import React from 'react';
import { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../../context/auth/AuthContext';
import { getMonthlyBillingSummary } from '../../sections/dashboard/Billing';
import { API_BASE_URL } from '../../config';

const icons = [
  // { icon: 'fas fa-trophy', label: 'Awards & Winners' }, // COMMENTED OUT
  { icon: 'fas fa-robot', label: 'Agents' },
  { icon: 'fas fa-book', label: 'Knowledge Base' },
  { icon: 'fas fa-phone', label: 'Phone Numbers' },
  // { icon: 'fas fa-check-circle', label: 'Verified Numbers' }, // COMMENTED OUT
  { icon: 'fas fa-list', label: 'Batch Call' },
  { icon: 'fas fa-history', label: 'Call History' },
  // { icon: 'fas fa-comments', label: 'Assistant Chat History' }, // COMMENTED OUT
  { icon: 'fas fa-chart-bar', label: 'Analytics' },
  // { icon: 'fas fa-credit-card', label: 'Billing' }, // Commented out - not needed
  // { icon: 'fas fa-question-circle', label: 'Contact Us' }, // Commented out - not needed
];

// Admin-only icons
const adminIcons = [
  { icon: 'fas fa-shield-alt', label: 'Admin Dashboard', isAdmin: true },
  { icon: 'fas fa-user-plus', label: 'Sign Up People', isAdmin: true },
  { icon: 'fas fa-book-open', label: 'Documentation', isAdmin: true },
  { icon: 'fas fa-ticket-alt', label: 'Promo Code', isAdmin: true },
  { icon: 'fas fa-robot', label: 'Retell Agents Details', isAdmin: true },
  { icon: 'fas fa-trash-alt', label: 'Deleted Records', isAdmin: true },
  { icon: 'fas fa-cog', label: 'Setup Billing', isAdmin: true }
];

const SidePanel = ({ sidebarOpen, setSidebarOpen, activePage, onPageChange, userName: propUserName, totalAmount }) => {
  const { logout, userEmail, userId, refreshUserId, userName: authUserName } = useAuth();
  const storedUser = typeof localStorage !== 'undefined' ? JSON.parse(localStorage.getItem('chatbotUser') || '{}') : {};
  const effectiveUserId = userId || storedUser?.userId || storedUser?.id || '';
  const effectiveEmail = (userEmail || storedUser?.email || '').toString().trim();
  
  // Use userName from AuthContext if prop is missing or same as email
  const userName = (propUserName && propUserName !== userEmail) ? propUserName : (authUserName || propUserName || '');
  
  // Debug logging
  console.log('SidePanel - userId:', userId);
  console.log('SidePanel - userEmail:', userEmail);
  console.log('SidePanel - userName:', userName);
  console.log('SidePanel - propUserName:', propUserName);
  console.log('SidePanel - authUserName:', authUserName);
  
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [receiveEmails, setReceiveEmails] = useState(true); // Toggle: receive all emails (default on)
  // const [helpCenterOpen, setHelpCenterOpen] = useState(false); // Commented out - Help Center not needed
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hello! I'm your AI assistant. How can I help you today?",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const userAvatar = userName ? userName[0].toUpperCase() : 'U';
  const userMenuRef = React.useRef();
  const userMenuMobileRef = React.useRef(); // Ref for mobile top sections
  const contactUsRef = React.useRef();
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [paygInvoice, setPaygInvoice] = useState(null);
  const [userFirstPayAmount, setUserFirstPayAmount] = useState(null);
  const [upcomingInvoiceAmount, setUpcomingInvoiceAmount] = useState(0); // users.first_pay – just display this
  const [agentCount, setAgentCount] = useState(null);
  const [durationTimeUsageMinutes, setDurationTimeUsageMinutes] = useState(null); // Duration Time Usage (total call mins) for Pay As You Go box
  const [callDetails, setCallDetails] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Upcoming Invoice: fetch users.first_pay for this user and display. No other tables.
  const fetchUpcomingInvoice = React.useCallback(() => {
    const stored = typeof localStorage !== 'undefined' ? JSON.parse(localStorage.getItem('chatbotUser') || '{}') : {};
    const uid = (userId || stored?.userId || stored?.id || '').toString().trim();
    const email = (userEmail || stored?.email || '').toString().trim();
    if (!uid && !email) return;
    const params = new URLSearchParams();
    if (uid) params.set('user_id', uid);
    if (email) params.set('user_email', email);
    const url = `${API_BASE_URL}/api/upcoming-invoice-amount?${params.toString()}`;
    fetch(url)
      .then(res => res.json())
      .then(data => {
        const a = data?.amount;
        if (a != null && !Number.isNaN(Number(a))) setUpcomingInvoiceAmount(Number(a));
      })
      .catch(() => {});
  }, [userEmail, userId]);

  useEffect(() => {
    fetchUpcomingInvoice();
    const t = setTimeout(fetchUpcomingInvoice, 2000);
    return () => clearTimeout(t);
  }, [fetchUpcomingInvoice]);

  // Fetch dynamic Pay As You Go invoice amount (simulate API call)
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('chatbotUser'));
    const email = user?.email;
    if (email) {
      fetch(`${API_BASE_URL}/api/cached-calls?user_email=${encodeURIComponent(email)}`)
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data)) {
            const monthlySummary = getMonthlyBillingSummary(data);
            const now = new Date();
            const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
            const currentMonthData = monthlySummary[currentMonthKey];
            if (currentMonthData && typeof currentMonthData.total_cost === 'number') {
              setPaygInvoice(currentMonthData.total_cost);
            } else {
              setPaygInvoice(0);
            }
          }
        });
    }
  }, []);

  // Fetch agent count (simulate API call)
  useEffect(() => {
    // Replace this with your real API call
    setTimeout(() => {
      setAgentCount(4); // Example: fetched agent count
    }, 500);
  }, []);

  // Fetch duration time usage from total_usage_by_email for Pay As You Go "Time Duration Usage" display
  useEffect(() => {
    const email = userEmail || (typeof localStorage !== 'undefined' && JSON.parse(localStorage.getItem('chatbotUser') || '{}')?.email);
    if (email) {
      fetch(`${API_BASE_URL}/api/user-duration-usage-from-table?user_email=${encodeURIComponent(email)}`)
        .then(res => res.ok ? res.json() : { total_minutes: 0 })
        .then(data => {
          if (data != null && typeof data.total_minutes === 'number') {
            setDurationTimeUsageMinutes(data.total_minutes);
          } else {
            setDurationTimeUsageMinutes(0);
          }
        })
        .catch(() => setDurationTimeUsageMinutes(0));
    }
  }, [userEmail]);

  // Fetch user email preference (receive emails toggle) - default on
  useEffect(() => {
    const email = userEmail || (typeof localStorage !== 'undefined' && JSON.parse(localStorage.getItem('chatbotUser') || '{}')?.email);
    if (email) {
      fetch(`${API_BASE_URL}/api/user-email-preference?user_email=${encodeURIComponent(email)}`)
        .then(res => res.ok ? res.json() : { receiveEmails: true })
        .then(data => {
          if (data && typeof data.receiveEmails === 'boolean') setReceiveEmails(data.receiveEmails);
        })
        .catch(() => {});
    }
  }, [userEmail]);

  const handleReceiveEmailsToggle = () => {
    const email = userEmail || (typeof localStorage !== 'undefined' && JSON.parse(localStorage.getItem('chatbotUser') || '{}')?.email);
    if (!email) return;
    const next = !receiveEmails;
    setReceiveEmails(next);
    fetch(`${API_BASE_URL}/api/user-email-preference`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_email: email, receiveEmails: next })
    }).catch(() => setReceiveEmails(receiveEmails));
  };

  // When user opens link from email (openProfile=1), open profile dropdown so they can toggle "Receive emails"
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('openProfile') === '1') {
      setUserMenuOpen(true);
      params.delete('openProfile');
      const newSearch = params.toString();
      const newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '') + (window.location.hash || '');
      window.history.replaceState(null, '', newUrl);
    }
  }, []);

  // Detect mobile screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    // Check on mount
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!userMenuOpen) return;
      
      // Check if click is on the popup itself
      const popup = document.querySelector('[data-popup="user-menu"]');
      const isClickOnPopup = popup && popup.contains(event.target);
      
      // Check if click is on the user menu ref (avatar/button) - check both desktop and mobile refs
      const isClickOnMenuRef = (userMenuRef.current && userMenuRef.current.contains(event.target)) || 
                                (userMenuMobileRef.current && userMenuMobileRef.current.contains(event.target));
      
      // If click is outside both the popup and the menu ref, close the popup
      if (!isClickOnPopup && !isClickOnMenuRef) {
        setUserMenuOpen(false);
      }
      
      // Help Center functionality commented out - not needed
      // if (helpCenterOpen && contactUsRef.current && !contactUsRef.current.contains(event.target)) {
      //   // Check if click is not on the popup itself
      //   const popup = document.querySelector('[data-popup="help-center"]');
      //   if (popup && !popup.contains(event.target)) {
      //     setHelpCenterOpen(false);
      //   }
      // }
    };

    // if (userMenuOpen || helpCenterOpen) { // Commented out helpCenterOpen
    if (userMenuOpen) {
      // Use mousedown and click events to ensure it works properly
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('click', handleClickOutside);
    };
  // }, [userMenuOpen, helpCenterOpen]); // Commented out helpCenterOpen
  }, [userMenuOpen]);

  // Calculate position for portal dropdown
  React.useEffect(() => {
    if (userMenuOpen) {
      // Check mobile ref first (if on mobile), then desktop ref
      const refToUse = isMobile && userMenuMobileRef.current ? userMenuMobileRef : userMenuRef;
      if (refToUse.current) {
        const rect = refToUse.current.getBoundingClientRect();
        // On mobile, position below the element; on desktop, position above
        const topPosition = isMobile ? rect.bottom + 8 : rect.top - 260;
        setMenuPos({ top: topPosition, left: rect.left });
      }
    }
  }, [userMenuOpen, isMobile]);

  // Chatbot functions
  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    // Simulate bot response
    setTimeout(() => {
      const botResponse = generateBotResponse(inputMessage);
      const botMessage = {
        id: messages.length + 2,
        text: botResponse,
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botMessage]);
      setIsTyping(false);
    }, 1000);
  };

  const generateBotResponse = (userInput) => {
    const input = userInput.toLowerCase();
    
    if (input.includes('billing') || input.includes('payment') || input.includes('invoice')) {
      return "For billing inquiries, you can check your current usage in the Billing section. If you have specific questions about charges, please provide your invoice number and I'll help you resolve it.";
    } else if (input.includes('technical') || input.includes('bug') || input.includes('error')) {
      return "I'm here to help with technical issues. Could you please describe the problem you're experiencing in detail? Include any error messages you're seeing.";
    } else if (input.includes('feature') || input.includes('request') || input.includes('suggestion')) {
      return "Thank you for your feedback! We're always looking to improve our platform. I'll forward your suggestion to our development team.";
    } else if (input.includes('account') || input.includes('login') || input.includes('password')) {
      return "For account-related issues, you can try resetting your password. If you're still having trouble, please contact our support team directly.";
    } else if (input.includes('hello') || input.includes('hi') || input.includes('hey')) {
      return "Hello! How can I assist you today? I can help with billing, technical issues, feature requests, and general questions about our platform.";
    } else {
      return "I understand you're asking about '" + userInput + "'. Could you please provide more details so I can better assist you? You can also contact our support team directly for immediate assistance.";
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      <style>{`
        @keyframes typing {
          0%, 60%, 100% {
            transform: translateY(0);
            opacity: 0.4;
          }
          30% {
            transform: translateY(-10px);
            opacity: 1;
          }
        }
      `}</style>

            {!sidebarOpen ? (
        // Collapsed sidebar: only icons and expand button
        <aside
          className="sidebar-collapsed"
          style={{
            width: 64, // 🔒 FIX: Fixed width to prevent changes when assistant panel opens
            minWidth: 64, // 🔒 FIX: Ensure minimum width is maintained
            maxWidth: 64, // 🔒 FIX: Ensure maximum width is maintained
            background: '#f8f9fb',
            borderRight: '1px solid #e5e7eb',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            height: '100%', // 🔒 FIX: Use 100% instead of 100vh to fill parent container properly
            minHeight: '100%', // 🔒 FIX: Ensure minimum height fills parent
            position: 'relative',
            zIndex: 10,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            flexShrink: 0, // 🔒 FIX: Prevent collapsed sidebar from shrinking when assistant panel opens
            boxSizing: 'border-box', // 🔒 FIX: Ensure padding/border are included in width calculation
            // Only enable vertical scroll on mobile
            ...(window.innerWidth <= 768 ? { maxHeight: '100%', overflowY: 'auto' } : {})
          }}
        >
        <button onClick={() => setSidebarOpen(true)} style={{ background: 'none', border: 'none', fontSize: '1.3rem', color: '#374151', cursor: 'pointer', margin: '20px 0 24px 0' }} title="Open sidebar">
          <i className="fas fa-chevron-right"></i>
        </button>
        
        {/* User avatar only (no name) in collapsed sidebar - fixed at top - MOBILE ONLY */}
        {isMobile && !(userEmail && userEmail.toLowerCase() === 'info@urcommunity.com.au') && (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            padding: '0 0 16px 0',
            marginBottom: '8px',
            cursor: 'pointer',
            width: '100%',
            position: 'relative'
          }} onClick={() => setUserMenuOpen(v => !v)} title={userName} ref={userMenuMobileRef}>
            <span 
              style={{ 
                background: '#0891b2', 
                color: '#fff', 
                borderRadius: '50%', 
                width: 40, 
                height: 40, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontWeight: 600, 
                fontSize: 18, 
                cursor: 'pointer' 
              }} 
              onClick={(e) => {
                e.stopPropagation();
                setUserMenuOpen(v => !v);
              }}
            >
              {userAvatar}
            </span>
            <i className="fas fa-chevron-down" style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}></i>
            {/* Dropdown menu for collapsed sidebar */}
            {userMenuOpen && ReactDOM.createPortal(
              <div data-popup="user-menu" style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 9999, background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.12)', minWidth: 280, maxWidth: '90vw', padding: '18px 0 8px 0', display: 'flex', flexDirection: 'column', gap: 0 }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '0 24px 12px 24px' }}>
                  <span style={{ background: '#0891b2', color: '#fff', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 24 }}>{userAvatar}</span>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontWeight: 600, fontSize: 16, color: '#222' }}>{userName || userEmail}</span>
                    <span style={{ fontSize: 14, color: '#64748b', marginTop: 2 }}>{userEmail}</span>
                  </div>
                </div>
                {/* Receive emails toggle - default on */}
                <div style={{ padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, color: '#222', fontSize: 15 }}>
                  <span><i className="fas fa-envelope" style={{ marginRight: 8, color: '#64748b' }}></i>Receive emails</span>
                  <button type="button" aria-label="Toggle receive emails" onClick={(e) => { e.stopPropagation(); handleReceiveEmailsToggle(); }} style={{ width: 44, height: 24, borderRadius: 12, border: 'none', background: receiveEmails ? '#0891b2' : '#cbd5e1', cursor: 'pointer', position: 'relative', flexShrink: 0 }}>
                    <span style={{ position: 'absolute', top: 2, left: receiveEmails ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s ease' }} />
                  </button>
                </div>
                <div 
                  style={{ padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', color: '#e11d48', fontWeight: 600, fontSize: 15 }}
                  onClick={() => {
                    logout();
                    setUserMenuOpen(false);
                    window.history.pushState({}, '', '/');
                    window.location.reload();
                  }}
                >
                  <i className="fas fa-sign-out-alt"></i> Logout
                </div>
              </div>, document.body
            )}
          </div>
        )}
        
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center', width: '100%' }}>
          {/* Admin options for admin users */}
          {userEmail && userEmail.toLowerCase() === 'info@urcommunity.com.au' ? (
            <>
              <button 
                onClick={() => onPageChange('Home')} 
                style={{ 
                  background: 'none', 
                  border: 'none',
                  color: activePage === 'Home' ? '#7c3aed' : '#374151', 
                  fontSize: '1.3rem', 
                  margin: 0, 
                  padding: 0, 
                  cursor: 'pointer', 
                  width: '100%' 
                }} 
                title="Agents"
              >
                <i className="fas fa-robot" style={{ color: activePage === 'Home' ? '#7c3aed' : '#374151' }}></i>
              </button>
              <button 
                onClick={() => onPageChange('Admin Dashboard')} 
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: activePage === 'Admin Dashboard' ? '#7c3aed' : '#dc2626', 
                  fontSize: '1.3rem', 
                  margin: 0, 
                  padding: 0, 
                  cursor: 'pointer', 
                  width: '100%' 
                }} 
                title="Admin Dashboard"
              >
                <i className="fas fa-shield-alt" style={{ color: activePage === 'Admin Dashboard' ? '#7c3aed' : '#dc2626' }}></i>
              </button>
              <button 
                onClick={() => onPageChange('Sign Up People')} 
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: activePage === 'Sign Up People' ? '#7c3aed' : '#dc2626', 
                  fontSize: '1.3rem', 
                  margin: 0, 
                  padding: 0, 
                  cursor: 'pointer', 
                  width: '100%' 
                }} 
                title="Sign Up People"
              >
                <i className="fas fa-user-plus" style={{ color: activePage === 'Sign Up People' ? '#7c3aed' : '#dc2626' }}></i>
              </button>
              <button 
                onClick={() => onPageChange('Documentation')} 
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: activePage === 'Documentation' ? '#7c3aed' : '#dc2626', 
                  fontSize: '1.3rem', 
                  margin: 0, 
                  padding: 0, 
                  cursor: 'pointer', 
                  width: '100%' 
                }} 
                title="Documentation"
              >
                <i className="fas fa-book-open" style={{ color: activePage === 'Documentation' ? '#7c3aed' : '#dc2626' }}></i>
              </button>
              <button 
                onClick={() => onPageChange('Promo Code')} 
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: activePage === 'Promo Code' ? '#7c3aed' : '#dc2626', 
                  fontSize: '1.3rem', 
                  margin: 0, 
                  padding: 0, 
                  cursor: 'pointer', 
                  width: '100%' 
                }} 
                title="Promo Code"
              >
                <i className="fas fa-ticket-alt" style={{ color: activePage === 'Promo Code' ? '#7c3aed' : '#dc2626' }}></i>
              </button>
              <button 
                onClick={() => onPageChange('Retell Agents Details')} 
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: activePage === 'Retell Agents Details' ? '#7c3aed' : '#dc2626', 
                  fontSize: '1.3rem', 
                  margin: 0, 
                  padding: 0, 
                  cursor: 'pointer', 
                  width: '100%' 
                }} 
                title="Retell Agents Details"
              >
                <i className="fas fa-robot" style={{ color: activePage === 'Retell Agents Details' ? '#7c3aed' : '#dc2626' }}></i>
              </button>
              <button 
                onClick={() => onPageChange('Deleted Records')} 
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: activePage === 'Deleted Records' ? '#7c3aed' : '#dc2626', 
                  fontSize: '1.3rem', 
                  margin: 0, 
                  padding: 0, 
                  cursor: 'pointer', 
                  width: '100%' 
                }} 
                title="Deleted Records"
              >
                <i className="fas fa-trash-alt" style={{ color: activePage === 'Deleted Records' ? '#7c3aed' : '#dc2626' }}></i>
              </button>
              <button 
                onClick={() => onPageChange('Setup Billing')} 
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: activePage === 'Setup Billing' ? '#7c3aed' : '#dc2626', 
                  fontSize: '1.3rem', 
                  margin: 0, 
                  padding: 0, 
                  cursor: 'pointer', 
                  width: '100%' 
                }} 
                title="Setup Billing"
              >
                <i className="fas fa-cog" style={{ color: activePage === 'Setup Billing' ? '#7c3aed' : '#dc2626' }}></i>
              </button>
            </>
          ) : (
            <>
              {/* Fixed Awards & Winners section - COMMENTED OUT */}
              {/* <button 
                onClick={() => window.open('/rewards', '_blank')} 
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  color: '#374151', 
                  fontSize: '1.3rem', 
                  margin: 0, 
                  padding: 0, 
                  cursor: 'pointer', 
                  width: '100%' 
                }} 
                title="Awards & Winners"
              >
                <i className="fas fa-trophy" style={{ color: '#374151' }}></i>
              </button> */}
              
              {/* Scrollable section starts from Agents */}
              {icons.map((item, idx) => {
                const isActive = activePage === (item.label === 'Agents' ? 'Home' : item.label);
                return (
                  <button key={item.label} onClick={() => onPageChange(item.label === 'Agents' ? 'Home' : item.label)} style={{ background: 'none', border: 'none', color: isActive ? '#7c3aed' : '#374151', fontSize: '1.3rem', margin: 0, padding: 0, cursor: 'pointer', width: '100%' }} title={item.label}>
                    <i className={item.icon} style={{ color: isActive ? '#7c3aed' : '#374151' }}></i>
                  </button>
                );
              })}
              
              {/* Billing V1 button for collapsed view - COMMENTED OUT */}
              {/* <button onClick={() => onPageChange('Billing V1')} style={{ background: 'none', border: 'none', color: activePage === 'Billing V1' ? '#7c3aed' : '#374151', fontSize: '1.3rem', margin: 0, padding: 0, cursor: 'pointer', width: '100%' }} title="Billing">
                <i className="fas fa-credit-card" style={{ color: activePage === 'Billing V1' ? '#7c3aed' : '#374151' }}></i>
              </button> */}
              
              {/* Admin-only icons */}
              {userEmail && userEmail.toLowerCase() === 'info@urcommunity.com.au' && (
                <>
                  <div style={{ height: '1px', background: '#e5e7eb', width: '80%', margin: '8px 0' }}></div>
                  {adminIcons.map((item, idx) => {
                    const isActive = activePage === item.label;
                    return (
                      <button key={item.label} onClick={() => onPageChange(item.label)} style={{ background: 'none', border: 'none', color: isActive ? '#7c3aed' : '#374151', fontSize: '1.3rem', margin: 0, padding: 0, cursor: 'pointer', width: '100%' }} title={item.label}>
                        <i className={item.icon} style={{ color: isActive ? '#7c3aed' : '#374151' }}></i>
                      </button>
                    );
                  })}
                </>
              )}
            </>
          )}
        </div>
        {/* User avatar in collapsed sidebar - hidden on mobile (shown above), visible on desktop */}
        {!isMobile && (
          <div style={{ marginBottom: '20px', cursor: 'pointer' }} onClick={() => setUserMenuOpen(v => !v)} title={userName}>
          <span 
            style={{ background: '#0891b2', color: '#fff', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 16, cursor: 'pointer' }} 
            onClick={(e) => {
              e.stopPropagation();
              setUserMenuOpen(v => !v);
            }}
          >
            {userAvatar}
          </span>
          {/* Dropdown menu for collapsed sidebar */}
          {userMenuOpen && ReactDOM.createPortal(
            <div data-popup="user-menu" style={{ position: 'fixed', top: '540px', left: '40px', zIndex: 9999, background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.12)', minWidth: 320, padding: '18px 0 8px 0', display: 'flex', flexDirection: 'column', gap: 0 }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '0 24px 12px 24px' }}>
                <span style={{ background: '#0891b2', color: '#fff', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 24 }}>{userAvatar}</span>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: 600, fontSize: 16, color: '#222' }}>{userName || userEmail}</span>
                  <span style={{ fontSize: 14, color: '#64748b', marginTop: 2 }}>{userEmail}</span>
                </div>
              </div>
              {/* Receive emails toggle - default on */}
              <div style={{ padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, color: '#222', fontSize: 15 }}>
                <span><i className="fas fa-envelope" style={{ marginRight: 8, color: '#64748b' }}></i>Receive emails</span>
                <button type="button" aria-label="Toggle receive emails" onClick={(e) => { e.stopPropagation(); handleReceiveEmailsToggle(); }} style={{ width: 44, height: 24, borderRadius: 12, border: 'none', background: receiveEmails ? '#0891b2' : '#cbd5e1', cursor: 'pointer', position: 'relative', flexShrink: 0 }}>
                  <span style={{ position: 'absolute', top: 2, left: receiveEmails ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s ease' }} />
                </button>
              </div>
              {/* Workspace - Commented out - not needed */}
              {/* <div style={{ padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', color: '#222', fontSize: 15 }}>
                <i className="fas fa-briefcase"></i> Workspace
              </div> */}
              {/* Light - Commented out - not needed */}
              {/* <div style={{ padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', color: '#222', fontSize: 15 }}>
                <i className="fas fa-sun"></i> Light
                <i className="fas fa-chevron-right" style={{ marginLeft: 'auto', fontSize: 13, color: '#64748b' }}></i>
              </div> */}
              {/* User ID - Commented out - not needed */}
              {/* <div style={{ padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', color: '#222', fontSize: 15 }}>
                <i className="fas fa-id-card"></i> User ID: {userId || 'Loading...'}
                {!userId && (
                  <button 
                    onClick={async (e) => {
                      e.stopPropagation();
                      await refreshUserId();
                    }}
                    style={{ 
                      marginLeft: 'auto', 
                      background: '#0891b2', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px', 
                      padding: '4px 8px', 
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    Refresh
                  </button>
                )}
              </div> */}
              <div 
                style={{ padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', color: '#e11d48', fontWeight: 600, fontSize: 15 }}
                onClick={() => {
                  logout();
                  setUserMenuOpen(false);
                  // Redirect to landing page
                  window.history.pushState({}, '', '/');
                  window.location.reload();
                }}
              >
                <i className="fas fa-sign-out-alt"></i> Logout
              </div>
            </div>, document.body
          )}
        </div>
        )}
        
        {/* Help Center Dropdown - Commented out - not needed */}
        {/* {helpCenterOpen && ReactDOM.createPortal(
          <div data-popup="help-center" style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 9999, background: '#fff', borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.12)', width: 320, padding: '8px 0', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            {/* Ask AI Evy *\/}
            <div style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, transition: 'background-color 0.2s' }} 
                 onMouseEnter={(e) => e.target.style.background = '#f8f9fb'} 
                 onMouseLeave={(e) => e.target.style.background = 'transparent'}
                 onClick={() => {
                   setHelpCenterOpen(false);
                   // Placeholder for AI chat
                   console.log('Ask AI Evy clicked');
                 }}>
              <i className="fas fa-robot" style={{ color: '#667eea', fontSize: '16px' }}></i>
              <span style={{ color: '#374151', fontSize: '14px', fontWeight: '500' }}>Ask AI Evy</span>
            </div>
            
            {/* Check Documentation *\/}
            <div style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, transition: 'background-color 0.2s' }} 
                 onMouseEnter={(e) => e.target.style.background = '#f8f9fb'} 
                 onMouseLeave={(e) => e.target.style.background = 'transparent'}
                 onClick={() => {
                   setHelpCenterOpen(false);
                   onPageChange('Knowledge Base');
                 }}>
              <i className="fas fa-file-alt" style={{ color: '#667eea', fontSize: '16px' }}></i>
              <span style={{ color: '#374151', fontSize: '14px', fontWeight: '500' }}>Check Documentation</span>
            </div>
            
            {/* Hire A Certified Partner *\/}
            <div style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, transition: 'background-color 0.2s' }} 
                 onMouseEnter={(e) => e.target.style.background = '#f8f9fb'} 
                 onMouseLeave={(e) => e.target.style.background = 'transparent'}
                 onClick={() => {
                   setHelpCenterOpen(false);
                   // Placeholder for partner modal
                   console.log('Hire A Certified Partner clicked');
                 }}>
              <i className="fas fa-certificate" style={{ color: '#667eea', fontSize: '16px' }}></i>
              <span style={{ color: '#374151', fontSize: '14px', fontWeight: '500' }}>Hire A Certified Partner</span>
            </div>
          </div>, document.body
        )} */}
      </aside>
      ) : (
        // Expanded sidebar
    <aside className="sidebar" style={{ 
      width: '250px', // 🔒 FIX: Fixed width to prevent changes when assistant panel opens
      minWidth: '250px', // 🔒 FIX: Ensure minimum width is maintained
      maxWidth: '250px', // 🔒 FIX: Ensure maximum width is maintained
      background: '#f8f9fb', 
      borderRight: '1px solid #e5e7eb', 
      padding: 0, 
      display: 'flex', 
      flexDirection: 'column', 
      position: 'relative', 
      height: '100%', 
      minHeight: '100%',
      flexShrink: 0, // 🔒 FIX: Prevent sidebar from shrinking when assistant panel opens
      boxSizing: 'border-box' // 🔒 FIX: Ensure padding/border are included in width calculation
    }}> {/* 🔒 FIX: Use 100% instead of 100vh to fill parent container properly */}
      {/* Hide scrollbar for sidebar */}
      <style>{`
        .sidebar::-webkit-scrollbar { display: none; }
        .sidebar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
      {/* Fixed header with close button and title */}
      <div className="sidebar-header" style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '8px', // 🔒 FIX: Reduced gap for better spacing
        padding: '20px 16px 16px 16px', // 🔒 FIX: Adjusted padding for better alignment
        marginBottom: '8px', // 🔒 FIX: Reduced margin for better spacing
        fontWeight: 'bold', 
        fontSize: '1.1rem', // 🔒 FIX: Slightly reduced font size to prevent wrapping
        letterSpacing: '0.5px', // 🔒 FIX: Reduced letter spacing
        color: '#2d3748', 
        background: '#f8f9fb', 
        position: 'sticky', 
        top: 0, 
        zIndex: 2, 
        minHeight: 56,
        whiteSpace: 'nowrap', // 🔒 FIX: Prevent text wrapping
        overflow: 'hidden', // 🔒 FIX: Hide overflow
        textOverflow: 'ellipsis' // 🔒 FIX: Add ellipsis if text is too long
      }}>
        <button onClick={() => setSidebarOpen(false)} style={{ 
          background: 'none', 
          border: 'none', 
          fontSize: '1.1rem', // 🔒 FIX: Match header font size
          color: '#374151', 
          cursor: 'pointer', 
          marginRight: 4, // 🔒 FIX: Reduced margin
          padding: '4px', // 🔒 FIX: Added padding for better click area
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0 // 🔒 FIX: Prevent button from shrinking
        }} title="Close sidebar">
          <i className="fas fa-chevron-left"></i>
        </button>
        <span style={{ 
          marginRight: '6px', // 🔒 FIX: Reduced margin
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0 // 🔒 FIX: Prevent icon from shrinking
        }}>
          <i className="fas fa-robot" style={{ 
            color: activePage === 'Home' ? '#7c3aed' : '#374151',
            fontSize: '1.2rem' // 🔒 FIX: Slightly larger icon for better visibility
          }}></i>
        </span>
        <span style={{
          whiteSpace: 'nowrap', // 🔒 FIX: Prevent text wrapping
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          flex: 1, // 🔒 FIX: Allow text to take available space
          minWidth: 0 // 🔒 FIX: Allow flex item to shrink below content size
        }}>ChatBot Factory</span>
      </div>
      
      {/* Fixed user avatar and name above Awards & Winners - MOBILE ONLY */}
      {isMobile && !(userEmail && userEmail.toLowerCase() === 'info@urcommunity.com.au') && (
        <div style={{ 
          padding: '0 24px 16px 24px',
          display: 'flex', 
          alignItems: 'center', 
          gap: 12,
          cursor: 'pointer',
          position: 'sticky',
          top: 56,
          zIndex: 2,
          background: '#f8f9fb',
          marginBottom: '8px'
        }} onClick={() => setUserMenuOpen(v => !v)} title={userName} ref={userMenuMobileRef}>
          <span 
            style={{ 
              background: '#0891b2', 
              color: '#fff', 
              borderRadius: '50%', 
              width: 36, 
              height: 36, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              fontWeight: 600, 
              fontSize: 16, 
              cursor: 'pointer',
              flexShrink: 0
            }} 
            onClick={(e) => {
              e.stopPropagation();
              setUserMenuOpen(v => !v);
            }}
          >
            {userAvatar}
          </span>
          <span style={{ 
            fontSize: '0.95rem', 
            color: '#374151', 
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1
          }} title={userName || userEmail}>
            {userName || userEmail}
          </span>
          <i className="fas fa-chevron-down" style={{ fontSize: 14, color: '#64748b', flexShrink: 0 }}></i>
          {/* Dropdown menu for expanded sidebar */}
          {userMenuOpen && ReactDOM.createPortal(
            <div data-popup="user-menu" style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 9999, background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.12)', minWidth: 280, maxWidth: '90vw', padding: '18px 0 8px 0', display: 'flex', flexDirection: 'column', gap: 0 }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '0 24px 12px 24px' }}>
                <span style={{ background: '#0891b2', color: '#fff', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 24 }}>{userAvatar}</span>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: 600, fontSize: 16, color: '#222' }}>{userName || userEmail}</span>
                  <span style={{ fontSize: 14, color: '#64748b', marginTop: 2 }}>{userEmail}</span>
                </div>
              </div>
              {/* Receive emails toggle - default on */}
              <div style={{ padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, color: '#222', fontSize: 15 }}>
                <span><i className="fas fa-envelope" style={{ marginRight: 8, color: '#64748b' }}></i>Receive emails</span>
                <button type="button" aria-label="Toggle receive emails" onClick={(e) => { e.stopPropagation(); handleReceiveEmailsToggle(); }} style={{ width: 44, height: 24, borderRadius: 12, border: 'none', background: receiveEmails ? '#0891b2' : '#cbd5e1', cursor: 'pointer', position: 'relative', flexShrink: 0 }}>
                  <span style={{ position: 'absolute', top: 2, left: receiveEmails ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s ease' }} />
                </button>
              </div>
              <div 
                style={{ padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', color: '#e11d48', fontWeight: 600, fontSize: 15 }}
                onClick={() => {
                  logout();
                  setUserMenuOpen(false);
                  window.history.pushState({}, '', '/');
                  window.location.reload();
                }}
              >
                <i className="fas fa-sign-out-alt"></i> Logout
              </div>
            </div>, document.body
          )}
        </div>
      )}
            {/* Scrollable nav and footer */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0, scrollbarWidth: 'none', paddingBottom: '24px' }} className="hide-scrollbar">
        <style>{`
          .hide-scrollbar::-webkit-scrollbar { display: none; }
          .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        `}</style>
        <nav className="sidebar-nav" style={{ flex: 1 }}>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {/* Check if user is admin */}
            {userEmail && userEmail.toLowerCase() === 'info@urcommunity.com.au' ? (
              /* Admin-only navigation */
              <>
                {/* Agents - For accessing Create Agent button */}
                <li className="sidebar-link" style={{ 
                  padding: '12px 24px', 
                  cursor: 'pointer', 
                  color: activePage === 'Home' ? '#7c3aed' : '#374151', 
                  background: activePage === 'Home' ? '#ede9fe' : 'transparent', 
                  borderRadius: '6px', 
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s ease'
                }} 
                onMouseEnter={(e) => {
                  if (activePage !== 'Home') {
                    e.currentTarget.style.background = '#f8f9fb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activePage !== 'Home') {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
                onClick={() => onPageChange('Home')}>
                  <i className="fas fa-robot" style={{ color: activePage === 'Home' ? '#7c3aed' : '#374151', fontSize: '1rem' }}></i> 
                  <span style={{ marginLeft: 10, fontSize: '0.95rem', fontWeight: activePage === 'Home' ? '600' : '500' }}>Agents</span>
                </li>
                {/* Admin Dashboard */}
                <li className="sidebar-link" style={{ 
                  padding: '12px 24px', 
                  cursor: 'pointer', 
                  color: activePage === 'Admin Dashboard' ? '#7c3aed' : '#374151', 
                  background: activePage === 'Admin Dashboard' ? '#ede9fe' : 'transparent', 
                  borderRadius: '6px', 
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s ease'
                }} 
                onMouseEnter={(e) => {
                  if (activePage !== 'Admin Dashboard') {
                    e.currentTarget.style.background = '#f8f9fb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activePage !== 'Admin Dashboard') {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
                onClick={() => onPageChange('Admin Dashboard')}>
                  <i className="fas fa-shield-alt" style={{ color: activePage === 'Admin Dashboard' ? '#7c3aed' : '#374151', fontSize: '1rem' }}></i> 
                  <span style={{ marginLeft: 10, fontSize: '0.95rem', fontWeight: activePage === 'Admin Dashboard' ? '600' : '500' }}>Admin Dashboard</span>
                </li>
                {/* Sign Up People */}
                <li className="sidebar-link" style={{ 
                  padding: '12px 24px', 
                  cursor: 'pointer', 
                  color: activePage === 'Sign Up People' ? '#7c3aed' : '#374151', 
                  background: activePage === 'Sign Up People' ? '#ede9fe' : 'transparent', 
                  borderRadius: '6px', 
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s ease'
                }} 
                onMouseEnter={(e) => {
                  if (activePage !== 'Sign Up People') {
                    e.currentTarget.style.background = '#f8f9fb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activePage !== 'Sign Up People') {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
                onClick={() => onPageChange('Sign Up People')}>
                  <i className="fas fa-user-plus" style={{ color: activePage === 'Sign Up People' ? '#7c3aed' : '#374151', fontSize: '1rem' }}></i> 
                  <span style={{ marginLeft: 10, fontSize: '0.95rem', fontWeight: activePage === 'Sign Up People' ? '600' : '500' }}>Sign Up People</span>
                </li>
                {/* Documentation */}
                <li className="sidebar-link" style={{ 
                  padding: '12px 24px', 
                  cursor: 'pointer', 
                  color: activePage === 'Documentation' ? '#7c3aed' : '#374151', 
                  background: activePage === 'Documentation' ? '#ede9fe' : 'transparent', 
                  borderRadius: '6px', 
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s ease'
                }} 
                onMouseEnter={(e) => { if (activePage !== 'Documentation') e.currentTarget.style.background = '#f8f9fb'; }}
                onMouseLeave={(e) => { if (activePage !== 'Documentation') e.currentTarget.style.background = 'transparent'; }}
                onClick={() => onPageChange('Documentation')}>
                  <i className="fas fa-book-open" style={{ color: activePage === 'Documentation' ? '#7c3aed' : '#374151', fontSize: '1rem' }}></i> 
                  <span style={{ marginLeft: 10, fontSize: '0.95rem', fontWeight: activePage === 'Documentation' ? '600' : '500' }}>Documentation</span>
                </li>
                {/* Promo Code */}
                <li className="sidebar-link" style={{ 
                  padding: '12px 24px', 
                  cursor: 'pointer', 
                  color: activePage === 'Promo Code' ? '#7c3aed' : '#374151', 
                  background: activePage === 'Promo Code' ? '#ede9fe' : 'transparent', 
                  borderRadius: '6px', 
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s ease'
                }} 
                onMouseEnter={(e) => {
                  if (activePage !== 'Promo Code') {
                    e.currentTarget.style.background = '#f8f9fb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activePage !== 'Promo Code') {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
                onClick={() => onPageChange('Promo Code')}>
                  <i className="fas fa-ticket-alt" style={{ color: activePage === 'Promo Code' ? '#7c3aed' : '#374151', fontSize: '1rem' }}></i> 
                  <span style={{ marginLeft: 10, fontSize: '0.95rem', fontWeight: activePage === 'Promo Code' ? '600' : '500' }}>Promo Code</span>
                </li>
                {/* Retell Agents Details */}
                <li className="sidebar-link" style={{ 
                  padding: '12px 24px', 
                  cursor: 'pointer', 
                  color: activePage === 'Retell Agents Details' ? '#7c3aed' : '#374151', 
                  background: activePage === 'Retell Agents Details' ? '#ede9fe' : 'transparent', 
                  borderRadius: '6px', 
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s ease'
                }} 
                onMouseEnter={(e) => {
                  if (activePage !== 'Retell Agents Details') {
                    e.currentTarget.style.background = '#f8f9fb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activePage !== 'Retell Agents Details') {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
                onClick={() => onPageChange('Retell Agents Details')}>
                  <i className="fas fa-robot" style={{ color: activePage === 'Retell Agents Details' ? '#7c3aed' : '#374151', fontSize: '1rem' }}></i> 
                  <span style={{ marginLeft: 10, fontSize: '0.95rem', fontWeight: activePage === 'Retell Agents Details' ? '600' : '500' }}>Retell Agents Details</span>
                </li>
                {/* Deleted Records */}
                <li className="sidebar-link" style={{ 
                  padding: '12px 24px', 
                  cursor: 'pointer', 
                  color: activePage === 'Deleted Records' ? '#7c3aed' : '#374151', 
                  background: activePage === 'Deleted Records' ? '#ede9fe' : 'transparent', 
                  borderRadius: '6px', 
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s ease'
                }} 
                onMouseEnter={(e) => {
                  if (activePage !== 'Deleted Records') {
                    e.currentTarget.style.background = '#f8f9fb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activePage !== 'Deleted Records') {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
                onClick={() => onPageChange('Deleted Records')}>
                  <i className="fas fa-trash-alt" style={{ color: activePage === 'Deleted Records' ? '#7c3aed' : '#374151', fontSize: '1rem' }}></i> 
                  <span style={{ marginLeft: 10, fontSize: '0.95rem', fontWeight: activePage === 'Deleted Records' ? '600' : '500' }}>Deleted Records</span>
                </li>
                {/* Setup Billing */}
                <li className="sidebar-link" style={{ 
                  padding: '12px 24px', 
                  cursor: 'pointer', 
                  color: activePage === 'Setup Billing' ? '#7c3aed' : '#374151', 
                  background: activePage === 'Setup Billing' ? '#ede9fe' : 'transparent', 
                  borderRadius: '6px', 
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s ease'
                }} 
                onMouseEnter={(e) => {
                  if (activePage !== 'Setup Billing') {
                    e.currentTarget.style.background = '#f8f9fb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activePage !== 'Setup Billing') {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
                onClick={() => onPageChange('Setup Billing')}>
                  <i className="fas fa-cog" style={{ color: activePage === 'Setup Billing' ? '#7c3aed' : '#374151', fontSize: '1rem' }}></i> 
                  <span style={{ marginLeft: 10, fontSize: '0.95rem', fontWeight: activePage === 'Setup Billing' ? '600' : '500' }}>Setup Billing</span>
                </li>
              </>
            ) : (
              /* Regular user navigation */
              <>
                {/* Fixed Awards & Winners section - COMMENTED OUT */}
                {/* <li className="sidebar-link" style={{ padding: '8px 24px', cursor: 'pointer', color: '#374151', background: 'transparent', borderRadius: '6px', marginBottom: '2px', position: 'sticky', top: 0, zIndex: 3, background: '#f8f9fb' }} onClick={() => window.open('/rewards', '_blank')}><i className="fas fa-trophy" style={{ color: '#374151' }}></i> <span style={{ marginLeft: 10 }}>Awards & Winners</span></li> */}
                
                {/* Scrollable section starts from Agents */}
                <div style={{ height: '1px', background: '#e5e7eb', margin: '4px 24px' }}></div>
                
                <li className="sidebar-link" style={{ 
                  padding: '12px 24px', 
                  cursor: 'pointer', 
                  color: activePage === 'Home' ? '#7c3aed' : '#374151', 
                  background: activePage === 'Home' ? '#ede9fe' : 'transparent', 
                  borderRadius: '6px', 
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s ease' // 🔒 FIX: Smooth transition
                }} 
                onMouseEnter={(e) => {
                  if (activePage !== 'Home') {
                    e.currentTarget.style.background = '#f8f9fb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activePage !== 'Home') {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
                onClick={() => onPageChange('Home')}>
                  <i className="fas fa-robot" style={{ color: activePage === 'Home' ? '#7c3aed' : '#374151', fontSize: '1rem' }}></i> 
                  <span style={{ marginLeft: 10, fontSize: '0.95rem', fontWeight: activePage === 'Home' ? '600' : '500' }}>Agents</span>
                </li>
                <li className="sidebar-link" style={{ 
                  padding: '12px 24px', 
                  cursor: 'pointer', 
                  color: activePage === 'Knowledge Base' ? '#7c3aed' : '#374151', 
                  background: activePage === 'Knowledge Base' ? '#ede9fe' : 'transparent', 
                  borderRadius: '6px', 
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s ease'
                }} 
                onMouseEnter={(e) => {
                  if (activePage !== 'Knowledge Base') {
                    e.currentTarget.style.background = '#f8f9fb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activePage !== 'Knowledge Base') {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
                onClick={() => onPageChange('Knowledge Base')}>
                  <i className="fas fa-book" style={{ color: activePage === 'Knowledge Base' ? '#7c3aed' : '#374151', fontSize: '1rem' }}></i> 
                  <span style={{ marginLeft: 10, fontSize: '0.95rem', fontWeight: activePage === 'Knowledge Base' ? '600' : '500' }}>Knowledge Base</span>
                </li>
                <li className="sidebar-link" style={{ 
                  padding: '12px 24px', 
                  cursor: 'pointer', 
                  color: activePage === 'Phone Numbers' ? '#7c3aed' : '#374151', 
                  background: activePage === 'Phone Numbers' ? '#ede9fe' : 'transparent', 
                  borderRadius: '6px', 
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s ease'
                }} 
                onMouseEnter={(e) => {
                  if (activePage !== 'Phone Numbers') {
                    e.currentTarget.style.background = '#f8f9fb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activePage !== 'Phone Numbers') {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
                onClick={() => onPageChange('Phone Numbers')}>
                  <i className="fas fa-phone" style={{ color: activePage === 'Phone Numbers' ? '#7c3aed' : '#374151', fontSize: '1rem' }}></i> 
                  <span style={{ marginLeft: 10, fontSize: '0.95rem', fontWeight: activePage === 'Phone Numbers' ? '600' : '500' }}>Phone Numbers</span>
                </li>
                {/* Verified Numbers - COMMENTED OUT */}
                {/* <li className="sidebar-link" style={{ 
                  padding: '12px 24px', 
                  cursor: 'pointer', 
                  color: activePage === 'Verified Numbers' ? '#7c3aed' : '#374151', 
                  background: activePage === 'Verified Numbers' ? '#ede9fe' : 'transparent', 
                  borderRadius: '6px', 
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s ease'
                }} 
                onMouseEnter={(e) => {
                  if (activePage !== 'Verified Numbers') {
                    e.currentTarget.style.background = '#f8f9fb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activePage !== 'Verified Numbers') {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
                onClick={() => onPageChange('Verified Numbers')}>
                  <i className="fas fa-check-circle" style={{ color: activePage === 'Verified Numbers' ? '#7c3aed' : '#374151', fontSize: '1rem' }}></i> 
                  <span style={{ marginLeft: 10, fontSize: '0.95rem', fontWeight: activePage === 'Verified Numbers' ? '600' : '500' }}>Verified Numbers</span>
                </li> */}
                <li className="sidebar-link" style={{ 
                  padding: '12px 24px', 
                  cursor: 'pointer', 
                  color: activePage === 'Batch Call' ? '#7c3aed' : '#374151', 
                  background: activePage === 'Batch Call' ? '#ede9fe' : 'transparent', 
                  borderRadius: '6px', 
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s ease'
                }} 
                onMouseEnter={(e) => {
                  if (activePage !== 'Batch Call') {
                    e.currentTarget.style.background = '#f8f9fb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activePage !== 'Batch Call') {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
                onClick={() => onPageChange('Batch Call')}>
                  <i className="fas fa-list" style={{ color: activePage === 'Batch Call' ? '#7c3aed' : '#374151', fontSize: '1rem' }}></i> 
                  <span style={{ marginLeft: 10, fontSize: '0.95rem', fontWeight: activePage === 'Batch Call' ? '600' : '500' }}>Batch Call</span>
                </li>
                <li className="sidebar-link" style={{ 
                  padding: '12px 24px', 
                  cursor: 'pointer', 
                  color: activePage === 'Call History' ? '#7c3aed' : '#374151', 
                  background: activePage === 'Call History' ? '#ede9fe' : 'transparent', 
                  borderRadius: '6px', 
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s ease'
                }} 
                onMouseEnter={(e) => {
                  if (activePage !== 'Call History') {
                    e.currentTarget.style.background = '#f8f9fb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activePage !== 'Call History') {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
                onClick={() => onPageChange('Call History')}>
                  <i className="fas fa-history" style={{ color: activePage === 'Call History' ? '#7c3aed' : '#374151', fontSize: '1rem' }}></i> 
                  <span style={{ marginLeft: 10, fontSize: '0.95rem', fontWeight: activePage === 'Call History' ? '600' : '500' }}>Call History</span>
                </li>
                {/* Assistant Chat History - COMMENTED OUT */}
                {/* <li className="sidebar-link" style={{ 
                  padding: '12px 24px', 
                  cursor: 'pointer', 
                  color: activePage === 'Assistant Chat History' ? '#7c3aed' : '#374151', 
                  background: activePage === 'Assistant Chat History' ? '#ede9fe' : 'transparent', 
                  borderRadius: '6px', 
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s ease'
                }} 
                onMouseEnter={(e) => {
                  if (activePage !== 'Assistant Chat History') {
                    e.currentTarget.style.background = '#f8f9fb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activePage !== 'Assistant Chat History') {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
                onClick={() => onPageChange('Assistant Chat History')}>
                  <i className="fas fa-comments" style={{ color: activePage === 'Assistant Chat History' ? '#7c3aed' : '#374151', fontSize: '1rem' }}></i> 
                  <span style={{ marginLeft: 10, fontSize: '0.95rem', fontWeight: activePage === 'Assistant Chat History' ? '600' : '500' }}>Assistant Chat History</span>
                </li> */}
                <li className="sidebar-link" style={{ 
                  padding: '12px 24px', 
                  cursor: 'pointer', 
                  color: activePage === 'Analytics' ? '#7c3aed' : '#374151', 
                  background: activePage === 'Analytics' ? '#ede9fe' : 'transparent', 
                  borderRadius: '6px', 
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s ease'
                }} 
                onMouseEnter={(e) => {
                  if (activePage !== 'Analytics') {
                    e.currentTarget.style.background = '#f8f9fb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activePage !== 'Analytics') {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
                onClick={() => onPageChange('Analytics')}>
                  <i className="fas fa-chart-bar" style={{ color: activePage === 'Analytics' ? '#7c3aed' : '#374151', fontSize: '1rem' }}></i> 
                  <span style={{ marginLeft: 10, fontSize: '0.95rem', fontWeight: activePage === 'Analytics' ? '600' : '500' }}>Analytics</span>
                </li>
                {/* <li className="sidebar-link" style={{ padding: '12px 24px', cursor: 'pointer', color: activePage === 'Billing' ? '#7c3aed' : '#374151', background: activePage === 'Billing' ? '#ede9fe' : 'transparent', borderRadius: '6px', marginBottom: '4px' }} onClick={() => onPageChange('Billing')}><i className="fas fa-credit-card" style={{ color: activePage === 'Billing' ? '#7c3aed' : '#374151' }}></i> <span style={{ marginLeft: 10 }}>Billing</span></li> */} {/* Commented out - not needed */}
                {/* Billing V1 link - COMMENTED OUT */}
                {/* <li className="sidebar-link" style={{ 
                  padding: '12px 24px', 
                  cursor: 'pointer', 
                  color: activePage === 'Billing V1' ? '#7c3aed' : '#374151', 
                  background: activePage === 'Billing V1' ? '#ede9fe' : 'transparent', 
                  borderRadius: '6px', 
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'all 0.2s ease'
                }} 
                onMouseEnter={(e) => {
                  if (activePage !== 'Billing V1') {
                    e.currentTarget.style.background = '#f8f9fb';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activePage !== 'Billing V1') {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
                onClick={() => onPageChange('Billing V1')}>
                  <i className="fas fa-credit-card" style={{ color: activePage === 'Billing V1' ? '#7c3aed' : '#374151', fontSize: '1rem' }}></i> 
                  <span style={{ marginLeft: 10, fontSize: '0.95rem', fontWeight: activePage === 'Billing V1' ? '600' : '500' }}>Billing</span>
                </li> */}
                
                {/* Admin-only section */}
                {userEmail && userEmail.toLowerCase() === 'info@urcommunity.com.au' && (
                  <>
                    <div style={{ height: '1px', background: '#e5e7eb', margin: '8px 24px' }}></div>
                    {adminIcons.map((item, idx) => {
                      const isActive = activePage === item.label;
                      return (
                        <li key={item.label} className="sidebar-link" style={{ padding: '12px 24px', cursor: 'pointer', color: isActive ? '#7c3aed' : '#374151', background: isActive ? '#ede9fe' : 'transparent', borderRadius: '6px', marginBottom: '4px' }} onClick={() => onPageChange(item.label)}>
                          <i className={item.icon} style={{ color: isActive ? '#7c3aed' : '#374151' }}></i> 
                          <span style={{ marginLeft: 10 }}>{item.label}</span>
                        </li>
                      );
                    })}
                  </>
                )}
                
                {/* <li className="sidebar-link" style={{ padding: '12px 24px', cursor: 'pointer', color: '#374151', borderRadius: '6px', marginBottom: '4px' }}><i className="fas fa-key"></i> <span style={{ marginLeft: 10 }}>Keys</span></li> */}
                {/* <li className="sidebar-link" style={{ padding: '12px 24px', cursor: 'pointer', color: '#374151', borderRadius: '6px', marginBottom: '4px' }}><i className="fas fa-link"></i> <span style={{ marginLeft: 10 }}>Webhooks</span></li> */}
              </>
            )}
          </ul>
        </nav>
        {/* Pay As You Go box, user info, and help center moved here */}
        <div style={{ padding: '0 24px', marginTop: '20px', marginBottom: '20px' }}>
          {/* Hide Pay As You Go box for admin users */}
          {!(userEmail && userEmail.toLowerCase() === 'info@urcommunity.com.au') && (
            <div style={{ background: '#e0e7ff', borderRadius: '8px', padding: '12px', marginBottom: '12px', color: '#1e40af', fontWeight: 'bold', fontSize: '0.95rem' }}>
              <span style={{ display: 'block', marginBottom: '4px' }}>Pay As You Go</span>
              <span style={{ fontWeight: 'normal', color: '#374151', fontSize: '0.9rem' }}>Upcoming Invoice: <b>${Number(upcomingInvoiceAmount).toFixed(2)}</b></span><br/>
              <span style={{ fontWeight: 'normal', color: '#374151', fontSize: '0.9rem' }}>Time Duration Usage: <b>{durationTimeUsageMinutes !== null ? `${durationTimeUsageMinutes} min` : <span style={{ color: '#aaa' }}>Loading...</span>}</b></span>
            </div>
          )}
          {/* User info at bottom - DESKTOP ONLY (on mobile, it's shown at top) */}
          {!isMobile && (
          <div style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, position: 'relative' }} onClick={() => setUserMenuOpen(v => !v)} ref={userMenuRef}>
            <span 
              style={{ background: '#0891b2', color: '#fff', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 18, cursor: 'pointer' }} 
              onClick={(e) => {
                e.stopPropagation();
                setUserMenuOpen(v => !v);
              }}
            >
              {userAvatar}
            </span>
            <span>{userName}</span>
            <i className="fas fa-chevron-down" style={{ fontSize: 14 }}></i>
            {/* Dropdown menu */}
            {userMenuOpen && ReactDOM.createPortal(
              <div data-popup="user-menu" style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 9999, background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.12)', minWidth: 320, padding: '18px 0 8px 0', display: 'flex', flexDirection: 'column', gap: 0 }} onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '0 24px 12px 24px' }}>
                    <span style={{ background: '#0891b2', color: '#fff', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 24 }}>{userAvatar}</span>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ fontWeight: 600, fontSize: 16, color: '#222' }}>{userName || userEmail}</span>
                      <span style={{ fontSize: 14, color: '#64748b', marginTop: 2 }}>{userEmail}</span>
                    </div>
                  </div>
                  {/* Receive emails toggle - default on */}
                  <div style={{ padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, color: '#222', fontSize: 15 }}>
                    <span><i className="fas fa-envelope" style={{ marginRight: 8, color: '#64748b' }}></i>Receive emails</span>
                    <button type="button" aria-label="Toggle receive emails" onClick={(e) => { e.stopPropagation(); handleReceiveEmailsToggle(); }} style={{ width: 44, height: 24, borderRadius: 12, border: 'none', background: receiveEmails ? '#0891b2' : '#cbd5e1', cursor: 'pointer', position: 'relative', flexShrink: 0 }}>
                      <span style={{ position: 'absolute', top: 2, left: receiveEmails ? 22 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s ease' }} />
                    </button>
                  </div>
                  {/* Workspace - Commented out - not needed */}
                  {/* <div style={{ padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', color: '#222', fontSize: 15 }}>
                    <i className="fas fa-briefcase"></i> Workspace
                  </div> */}
                  {/* Light - Commented out - not needed */}
                  {/* <div style={{ padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', color: '#222', fontSize: 15 }}>
                    <i className="fas fa-sun"></i> Light
                    <i className="fas fa-chevron-right" style={{ marginLeft: 'auto', fontSize: 13, color: '#64748b' }}></i>
                  </div> */}
                  {/* User ID - Commented out - not needed */}
                  {/* <div style={{ padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', color: '#222', fontSize: 15 }}>
                    <i className="fas fa-id-card"></i> User ID: {userId || 'Loading...'}
                    {!userId && (
                      <button 
                        onClick={async (e) => {
                          e.stopPropagation();
                          await refreshUserId();
                        }}
                        style={{ 
                          marginLeft: 'auto', 
                          background: '#0891b2', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: '4px', 
                          padding: '4px 8px', 
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Refresh
                      </button>
                    )}
                  </div> */}
                  <div 
                    style={{ padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', color: '#e11d48', fontWeight: 600, fontSize: 15 }}
                    onClick={() => {
                      logout();
                      setUserMenuOpen(false);
                      // Redirect to landing page
                      window.history.pushState({}, '', '/');
                      window.location.reload();
                    }}
                  >
                    <i className="fas fa-sign-out-alt"></i> Logout
                  </div>
                </div>, document.body
              )}
            </div>
          )}
            {/* Help Center - Commented out - not needed */}
            {/* {!(userEmail && userEmail.toLowerCase() === 'info@urcommunity.com.au') && (
              <li className="sidebar-link" style={{ padding: '12px 24px', cursor: 'pointer', color: '#374151', background: 'transparent', borderRadius: '6px', marginBottom: '4px', listStyle: 'none' }} onClick={() => setHelpCenterOpen(v => !v)} ref={contactUsRef}><i className="fas fa-info-circle" style={{ color: '#374151' }}></i> <span style={{ marginLeft: 10 }}>Help Center</span></li>
            )} */}
          </div>
        </div>
        
        {/* Contact Us Popup */}
        {/* This section is no longer needed as contactUsOpen, setContactUsOpen, contactTab, setContactTab are removed */}
      </aside>
      )}
    </>
  );
};

export default SidePanel; 